import { ProfilesList } from "@/components/profiles/ProfilesList";
import ErrorDisplay from "@/components/ErrorDisplay";
import { useProfiles } from "@/hooks/useProfiles";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ViewToggle from "@/components/ViewToggle";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function ConfigurationsPage() {
  const { profiles, loading, error } = useProfiles();
  const [view, setView] = useState<"grid" | "list">(() => {
    const savedView = localStorage.getItem("configurationsViewPreference");
    return (savedView === "list" || savedView === "grid") ? savedView : "list";
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "deployed" | "draft">("all");

  const filteredProfiles = profiles.filter(profile => {
    const matchesSearch = profile.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         profile.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || 
                         (statusFilter === "draft" && profile.isDraft) ||
                         (statusFilter === "deployed" && !profile.isDraft);
    
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    localStorage.setItem("configurationsViewPreference", view);
  }, [view]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background w-full overflow-hidden">
        <div className="px-6 py-6">
          <div className="text-left mb-8">
            <div className="h-8 w-48 bg-muted rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-muted rounded animate-pulse"></div>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border rounded-lg p-6 space-y-4">
                <div className="h-6 w-2/3 bg-muted rounded animate-pulse"></div>
                <div className="h-4 w-full bg-muted rounded animate-pulse"></div>
                <div className="h-4 w-1/2 bg-muted rounded animate-pulse"></div>
                <div className="flex justify-between pt-4">
                  <div className="h-9 w-20 bg-muted rounded animate-pulse"></div>
                  <div className="h-9 w-20 bg-muted rounded animate-pulse"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background w-full overflow-hidden">
        <div className="px-6 py-6">
          <ErrorDisplay error={error} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background w-full overflow-hidden">
      <div className="px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Configuration Profiles</h1>
            <p className="text-muted-foreground mt-1">
              Manage device configuration profiles
            </p>
          </div>
          <div className="flex items-center gap-4">
            <Button asChild>
              <Link to="/configurations/new">
                <Plus className="mr-2 h-4 w-4" />
                New Configuration Profile
              </Link>
            </Button>
          </div>
        </div>

        {/* Filters and Stats */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search profiles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-72"
              />
            </div>
            <Select value={statusFilter} onValueChange={(value: "all" | "deployed" | "draft") => setStatusFilter(value)}>
              <SelectTrigger className="w-40">
                <Filter className="mr-2 h-4 w-4" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Profiles</SelectItem>
                <SelectItem value="deployed">Deployed</SelectItem>
                <SelectItem value="draft">Drafts</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">{profiles.length}</Badge>
                <span>Total</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{profiles.filter(p => p.isDraft).length}</Badge>
                <span>Drafts</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="default">{profiles.filter(p => !p.isDraft).length}</Badge>
                <span>Deployed</span>
              </div>
            </div>
            <ViewToggle view={view} onViewChange={setView} />
          </div>
        </div>

        {/* Profiles List */}
        <ProfilesList profiles={filteredProfiles} view={view} searchQuery="" />
      </div>
    </div>
  );
}
